// @ts-nocheck
import {useEffect, useId, useRef} from "react";
import {useTranslation} from "react-i18next";
import {BaseBreadcrumb} from "@/components";
import LoadingLogo from "../../../components/LoadingLogo";
import {trackGameStart} from "../../../providers/analytics";
import {useBingoStore} from "../../../store/useBingo.store";
import useCurrencyStore from "../../../store/useCurrency.store";
import "../../../styles/full-viewport-iframe.css";

const BingoScreen = () => {
  const {t} = useTranslation();

  const startBingoGame = useBingoStore(state => state.startBingoGame);
  const gameUrl = useBingoStore(state => state.gameUrl);
  const loading = useBingoStore(state => state.loading);
  const unload = useBingoStore(state => state.unload);
  const error = useBingoStore(state => state.error);
  const screenMode = useBingoStore(state => state.screenMode);
  const setScreenMode = useBingoStore(state => state.setScreenMode);
  const resetScreenModeToDefault = useBingoStore(state => state.resetScreenModeToDefault);

  const selectedCurrency = useCurrencyStore(state => state.selectedCurrency);

  useEffect(() => {
    const handleResize = () => {
      // Reset to default screen mode based on new screen size
      resetScreenModeToDefault();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [resetScreenModeToDefault]);

  const iFrameRef = useRef(null);
  const iframeId = useId();

  useEffect(() => {
    // Start the bingo game when component mounts
    const currency = selectedCurrency?.id || "VES";
    startBingoGame(currency);
    trackGameStart("bingo", "elinmejorable");

    // Force fullscreen mode for mobile when entering the game
    if (window.innerWidth <= 768) {
      setScreenMode("fullscreen");
    }

    return () => {
      unload();
      // Wait for the element to be available
      const bottomNavMenu = document.querySelector(".bottomNavMenu");
      if (bottomNavMenu) {
        bottomNavMenu.style.visibility = "visible";
      }
    };
  }, [selectedCurrency, startBingoGame, setScreenMode, unload]);

  useEffect(() => {
    if (iFrameRef.current) {
      iFrameRef.current.focus(); // Set focus when the iframe loads
      iFrameRef.current.scrollIntoView({behavior: "smooth", block: "center"});
    }

    if (gameUrl) {
      if (screenMode === "fullscreen") {
        setTimeout(() => {
          window.scrollTo(0, 1);
        }, 100);
      } else {
        // For centered mode, scroll to center the iframe
        setTimeout(() => {
          if (iFrameRef.current) {
            iFrameRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }
    }
  }, [gameUrl, screenMode]); // Trigger the effect when gameUrl or screenMode changes

  const headerHeight = document.querySelector("header")?.offsetHeight || 0; // Get header height

  // Handle bottom navigation visibility based on screen mode
  useEffect(() => {
    const bottomNavMenu = document.querySelector(".bottomNavMenu");
    if (bottomNavMenu) {
      if (screenMode === "fullscreen") {
        bottomNavMenu.style.visibility = "hidden";
      } else {
        bottomNavMenu.style.visibility = "visible";
      }
    }
  }, [screenMode, gameUrl]);

  const toggleScreenMode = () => {
    const newMode = screenMode === "fullscreen" ? "centered" : "fullscreen";
    setScreenMode(newMode);
  };

  return (
    <div
      className={`full-viewport-iframe ${screenMode === "fullscreen" ? "fullscreen" : "centered"}`}
      style={{
        position: screenMode === "fullscreen" ? "fixed" : "relative",
        top: screenMode === "fullscreen" ? 0 : "auto",
        left: screenMode === "fullscreen" ? 0 : "auto",
        width: screenMode === "fullscreen" ? "100vw" : "100%",
        height: screenMode === "fullscreen" ? "100vh" : `calc(100vh - ${headerHeight}px)`,
        zIndex: screenMode === "fullscreen" ? 1000 : "auto",
        backgroundColor: "#000",
      }}
    >
      {screenMode !== "fullscreen" && (
        <div className="px-4 pt-3">
          <BaseBreadcrumb items={[{label: "Bingo"}]} />
        </div>
      )}
      {/* Fullscreen Controls */}
      {screenMode !== "fullscreen" && window.innerWidth > 768 && (
        <button
          type="button"
          onClick={toggleScreenMode}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1001,
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            border: "1px solid var(--color-secondary)",
            borderRadius: "var(--radius-sm)",
            padding: "8px 12px",
            cursor: "pointer",
            fontSize: "var(--font-body)",
          }}
        >
          {t("enterFullscreen", "Enter Fullscreen")}
        </button>
      )}
      {screenMode === "fullscreen" && (
        <button
          type="button"
          onClick={toggleScreenMode}
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1001,
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            border: "1px solid var(--color-secondary)",
            borderRadius: "var(--radius-sm)",
            padding: "8px 12px",
            cursor: "pointer",
            fontSize: "var(--font-body)",
          }}
        >
          {t("exitFullscreen", "Exit Fullscreen")}
        </button>
      )}

      {error && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "white",
            textAlign: "center",
          }}
        >
          <h3>{t("gameLoadError", "We had an issue loading the bingo game.")}</h3>
        </div>
      )}

      {loading || !gameUrl ? (
        <LoadingLogo />
      ) : (
        <iframe
          id={iframeId}
          ref={iFrameRef}
          src={gameUrl}
          frameBorder="0"
          title={t("bingoGame", "Bingo Game")}
          allow="clipboard-read; clipboard-write; fullscreen; screen-wake-lock; accelerometer; gyroscope; magnetometer; microphone; camera; geolocation"
          style={{
            width: "100%",
            height: screenMode === "fullscreen" ? "100vh" : `calc(100vh - ${headerHeight}px)`,
          }}
        />
      )}
    </div>
  );
};

export default BingoScreen;
